import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { jobCardsApi, deliverablesApi, clientsApi, employeesApi } from "@/services/api";
import ActivityTimeline from "@/pages/jobcards/components/ActivityTimeline";

interface Client {
  id: number;
  name: string;
  company?: string;
}

const statusColors: Record<string, string> = {
  "Pending":     "bg-gray-100 text-gray-600",
  "In Progress": "bg-amber-100 text-amber-700",
  "Completed":   "bg-amber-100 text-amber-700",
  "Delivered":   "bg-gray-800 text-white",
};

const priorityColors: Record<string, string> = {
  "Low":      "bg-gray-100 text-gray-500",
  "Medium":   "bg-amber-100 text-amber-600",
  "High":     "bg-orange-100 text-orange-600",
  "Critical": "bg-red-100 text-red-600",
};

const statusSteps = ["Pending", "In Progress", "Completed", "Delivered"];

const SERVICE_OPTIONS = [
  { label: "Topographic Survey", icon: "ri-map-2-line" },
  { label: "DGPS Survey", icon: "ri-radar-line" },
  { label: "Boundary Survey", icon: "ri-focus-3-line" },
  { label: "Soil Test", icon: "ri-flask-line" },
  { label: "As-Built Survey", icon: "ri-building-2-line" },
  { label: "Drone Survey", icon: "ri-flight-takeoff-line" },
  { label: "Revenue Survey", icon: "ri-government-line" },
  { label: "Hydrogeological Survey", icon: "ri-water-flash-line" },
  { label: "CAD Drafting", icon: "ri-pen-nib-line" },
  { label: "Custom Service", icon: "ri-add-circle-line" },
];

const fileTypeIcon: Record<string, { icon: string; color: string }> = {
  PDF:  { icon: "ri-file-pdf-2-line",   color: "#ef4444" },
  DWG:  { icon: "ri-file-code-line",    color: "#f59e0b" },
  KMZ:  { icon: "ri-map-pin-2-line",    color: "#10b981" },
  KML:  { icon: "ri-map-pin-2-line",    color: "#10b981" },
  XLSX: { icon: "ri-file-excel-2-line", color: "#16a34a" },
  ZIP:  { icon: "ri-folder-zip-line",   color: "#f59e0b" },
  DXF:  { icon: "ri-file-code-line",    color: "#6366f1" },
  SHP:  { icon: "ri-map-2-line",        color: "#8b5cf6" },
  JPG:  { icon: "ri-image-line",        color: "#ec4899" },
};

const deliverableStatusColors: Record<string, string> = {
  "Draft":        "bg-gray-100 text-gray-600",
  "Under Review": "bg-amber-100 text-amber-700",
  "Approved":     "bg-amber-100 text-amber-700",
  "Delivered":    "bg-gray-800 text-white",
};

interface JobCard {
  id: number;
  jobNo: string;
  title: string;
  client: string;
  clientId: number;
  clientContact: string;
  contactPersonPhone: string;
  location: string;
  services: string[];
  status: string;
  assignedDate: string;
  createdDate: string;
  dueDate: string;
  fieldTeam: string[];
  officeTeam: string[];
  fieldTeamIds: number[];
  officeTeamIds: number[];
  progress: number;
  priority: string;
  area: string;
  remarks: string;
}

interface Deliverable {
  id: number;
  jobNo: string;
  jobId: number;
  jobTitle: string;
  fileName: string;
  fileType: string;
  category: string;
  version: string;
  status: string;
  size: string;
  uploadedBy: string;
  uploadedAt: string;
  downloadCount: number;
  isLatest: boolean;
}

/** Convert any date string (ISO datetime or plain date) to yyyy-MM-dd for <input type="date"> */
function toDateStr(val: unknown): string {
  if (!val) return "";
  const s = String(val);
  // Already yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO datetime like 2026-04-17T18:30:00.000Z — take the date part
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  return "";
}

/** Normalize DB status (lowercase/snake) → display title case */
function normalizeStatus(s: unknown): string {
  const map: Record<string, string> = {
    pending:     "Pending",
    in_progress: "In Progress",
    "in progress": "In Progress",
    completed:   "Completed",
    delivered:   "Delivered",
    on_hold:     "On Hold",
    cancelled:   "Cancelled",
  };
  const key = String(s ?? "").toLowerCase().trim();
  return map[key] ?? String(s ?? "Pending");
}

/** Normalize DB priority (lowercase) → display title case */
function normalizePriority(p: unknown): string {
  const map: Record<string, string> = {
    low:      "Low",
    medium:   "Medium",
    high:     "High",
    critical: "Critical",
  };
  const key = String(p ?? "").toLowerCase().trim();
  return map[key] ?? String(p ?? "Medium");
}

/** Map DB snake_case columns → frontend camelCase shape used throughout this page */
function normalizeJobCard(raw: Record<string, unknown>): JobCard {
  // services: array of {service_name} objects OR array of strings — filter empty
  const rawServices = (raw.services as Array<Record<string, unknown> | string>) ?? [];
  const services = rawServices
    .map((s) => typeof s === "string" ? s : String((s as Record<string, unknown>).service_name ?? ""))
    .filter((s) => s.trim() !== "");

  // team: array of {employee_name, team_type} objects
  const rawTeam = (raw.team as Array<Record<string, unknown>>) ?? [];
  const fieldTeam = rawTeam
    .filter((t) => t.team_type === "field")
    .map((t) => String(t.employee_name ?? ""))
    .filter(Boolean);
  const officeTeam = rawTeam
    .filter((t) => t.team_type === "office")
    .map((t) => String(t.employee_name ?? ""))
    .filter(Boolean);

  // Extract employee IDs too for editing
  const fieldTeamIds = rawTeam
    .filter((t) => t.team_type === "field")
    .map((t) => Number(t.user_id ?? 0))
    .filter((id) => id > 0);
  const officeTeamIds = rawTeam
    .filter((t) => t.team_type === "office")
    .map((t) => Number(t.user_id ?? 0))
    .filter((id) => id > 0);

  // client_contact is the actual DB column name (not contact_person)
  const clientContact = String(
    raw.client_contact ?? raw.contact_person ?? raw.clientContact ?? ""
  );
  const contactPersonPhone = String(
    raw.contact_person_phone ?? raw.contactPersonPhone ?? ""
  );

  return {
    id: Number(raw.id),
    jobNo: String(raw.job_no ?? raw.jobNo ?? ""),
    title: String(raw.title ?? ""),
    client: String(raw.client_name ?? raw.client ?? ""),
    clientId: Number(raw.client_id ?? raw.clientId ?? 0),
    clientContact,
    contactPersonPhone,
    location: String(raw.location ?? ""),
    services,
    status: normalizeStatus(raw.status),
    assignedDate: toDateStr(raw.assigned_date ?? raw.assignedDate),
    createdDate: toDateStr(raw.created_at ?? raw.createdDate),
    dueDate: toDateStr(raw.due_date ?? raw.dueDate),
    fieldTeam,
    officeTeam,
    fieldTeamIds,
    officeTeamIds,
    progress: Number(raw.progress ?? 0),
    priority: normalizePriority(raw.priority),
    area: String(raw.area ?? ""),
    remarks: String(raw.remarks ?? ""),
  };
}

function JobDeliverablesTab({ jobId, jobNo, deliverables }: { jobId: number; jobNo: string; deliverables: Deliverable[] }) {
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [downloadedIds, setDownloadedIds] = useState<number[]>([]);

  const statuses = ["All", "Draft", "Under Review", "Approved", "Delivered"];
  const categories = ["All", ...Array.from(new Set(deliverables.map((d) => d.category)))];

  const filtered = deliverables.filter((d) => {
    const matchStatus = statusFilter === "All" || d.status === statusFilter;
    const matchCat = categoryFilter === "All" || d.category === categoryFilter;
    return matchStatus && matchCat;
  });

  const statusCounts = {
    Draft: deliverables.filter((d) => d.status === "Draft").length,
    "Under Review": deliverables.filter((d) => d.status === "Under Review").length,
    Approved: deliverables.filter((d) => d.status === "Approved").length,
    Delivered: deliverables.filter((d) => d.status === "Delivered").length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Draft",        count: statusCounts.Draft,           color: "#6b7280", bg: "#f3f4f6" },
          { label: "Under Review", count: statusCounts["Under Review"], color: "#d97706", bg: "#fffbeb" },
          { label: "Approved",     count: statusCounts.Approved,        color: "#16a34a", bg: "#ecfdf5" },
          { label: "Delivered",    count: statusCounts.Delivered,       color: "#1f2937", bg: "#f9fafb" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.count}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {statuses.map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${statusFilter === s ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                  {s}
                </button>
              ))}
            </div>
            {categories.length > 1 && (
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 cursor-pointer">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            )}
          </div>
          <label className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-amber-600 whitespace-nowrap transition-all">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></span>
            Upload File
            <input type="file" className="hidden" />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-gray-800">Files for {jobNo}</h3>
          <span className="text-xs text-gray-400">{filtered.length} file{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-folder-open-line text-4xl text-gray-200" />
            </div>
            <p className="text-sm text-gray-400">No deliverables found for this job</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((file) => {
              const ft = fileTypeIcon[file.fileType] || { icon: "ri-file-line", color: "#6b7280" };
              const isDownloaded = downloadedIds.includes(file.id);
              return (
                <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-amber-100 hover:bg-amber-50/20 transition-all group">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-gray-100 flex-shrink-0">
                    <i className={`${ft.icon} text-xl`} style={{ color: ft.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.fileName}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-400">{file.category}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{file.version}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{file.size}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{file.uploadedBy}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${deliverableStatusColors[file.status] || "bg-gray-100 text-gray-600"}`}>
                      {file.status}
                    </span>
                    <button onClick={() => setDownloadedIds((p) => [...p, file.id])}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                        isDownloaded ? "bg-amber-100 text-amber-700" : "bg-white border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-600"
                      }`}>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className={`${isDownloaded ? "ri-checkbox-circle-line" : "ri-download-2-line"} text-xs`} />
                      </span>
                      {isDownloaded ? "Downloaded" : "Download"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end">
        <Link to="/deliverables" className="flex items-center gap-1.5 text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer whitespace-nowrap">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-xs" /></span>
          Manage all deliverables →
        </Link>
      </div>
    </div>
  );
}

export default function JobCardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [job, setJob] = useState<JobCard | null>(null);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState<"overview" | "timeline" | "notes" | "files" | "deliverables">("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<JobCard>>({});
  const [notes, setNotes] = useState<{ id: number; author: string; avatar: string; date: string; text: string }[]>([]);
  const [newNote, setNewNote] = useState("");
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Array<{ id: number; name: string; role: string; department: string; status: string; avatar: string }>>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const fetchJob = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const raw = await jobCardsApi.getById(Number(id));
      const data = normalizeJobCard(raw as Record<string, unknown>);
      setJob(data);
      setEditData(data);
      setStatus(data.status);
      setProgress(data.progress);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(errMsg);
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchDeliverables = useCallback(async () => {
    try {
      const data = await deliverablesApi.getAll({ job_id: Number(id) });
      setDeliverables(Array.isArray(data) ? data : data.data ?? []);
    } catch {
      setDeliverables([]);
    }
  }, [id]);

  const fetchClients = useCallback(async () => {
    try {
      const data = await clientsApi.getAll({ limit: 1000 });
      setClients(Array.isArray(data) ? data : data.data ?? []);
    } catch {
      setClients([]);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const res = await employeesApi.getAll() as { data?: Array<Record<string, unknown>>; employees?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>;
      const raw = Array.isArray(res) ? res : (res.data || res.employees || []);
      const mapped = raw.map((e: Record<string, unknown>) => ({
        id: Number(e.id || 0),
        name: String(e.name || ""),
        role: String(e.role || e.designation || ""),
        department: String(e.department || ""),
        status: String(e.status || "active"),
        avatar: String(e.name || "??").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase(),
      })).filter((e) => e.status.toLowerCase() === "active" || e.status.toLowerCase() === "Active");
      setEmployees(mapped);
    } catch {
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  // Fetch job notes from timeline API
  const fetchNotes = useCallback(async () => {
    try {
      const raw = await jobCardsApi.getById(Number(id));
      const timeline = (raw as Record<string, unknown>).timeline as Array<Record<string, unknown>> || [];
      const mappedNotes = timeline
        .filter((t) => t.note && String(t.note).trim() !== "")
        .map((t, idx) => ({
          id: idx + 1,
          author: String(t.changed_by_name || "System"),
          avatar: String(t.changed_by_name || "S").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase(),
          date: toDateStr(t.changed_at),
          text: String(t.note),
        }));
      setNotes(mappedNotes);
    } catch {
      setNotes([]);
    }
  }, [id]);

  useEffect(() => {
    fetchJob();
    fetchDeliverables();
    fetchClients();
    fetchNotes();
    fetchEmployees();
  }, [fetchJob, fetchDeliverables, fetchClients, fetchNotes, fetchEmployees]);

  const handleSave = async () => {
    if (!job) return;
    setSaving(true);
    setError("");
    try {
      // Build payload with correct field names for backend
      const payload = {
        title: editData.title ?? job.title,
        client_id: editData.clientId ?? job.clientId,
        client_contact: editData.clientContact ?? job.clientContact,
        contact_person_phone: editData.contactPersonPhone ?? job.contactPersonPhone,
        location: editData.location ?? job.location,
        priority: editData.priority ?? job.priority,
        assigned_date: editData.assignedDate ?? job.assignedDate,
        due_date: editData.dueDate ?? job.dueDate,
        area: editData.area ?? job.area,
        remarks: editData.remarks ?? job.remarks,
        progress,
        services: (editData.services ?? job.services).map((name: string) => ({
          name,
          qty: 1,
          unit: 'lump sum',
          rate: 0
        })),
        field_team: (editData as Record<string, unknown>).fieldTeamIds ?? job.fieldTeamIds,
        office_team: (editData as Record<string, unknown>).officeTeamIds ?? job.officeTeamIds,
      };
      // Update main job details (including services and team)
      await jobCardsApi.update(job.id, payload);
      // Update status separately if it changed (compare normalized values)
      if (normalizeStatus(status) !== normalizeStatus(job.status)) {
        await jobCardsApi.updateStatus(job.id, status);
      }
      // Re-fetch to get fresh data from server
      await fetchJob();
      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save job card";
      setError(message);
      // Don't exit edit mode on error so user can retry
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!job || !isEditing) return;
    setStatus(newStatus);
    // Status will be saved when user clicks Save Changes
  };

  const addNote = async () => {
    if (!newNote.trim() || !job) return;
    try {
      // Add note via status update API with note
      await jobCardsApi.updateStatus(job.id, status, newNote);
      // Refresh notes from server
      await fetchNotes();
      setNewNote("");
    } catch {
      // Fallback: add locally if API fails
      setNotes((prev) => [...prev, {
        id: Date.now(),
        author: "Current User",
        avatar: "CU",
        date: new Date().toISOString().split("T")[0],
        text: newNote,
      }]);
      setNewNote("");
    }
  };

  if (loading) {
    return (
      <ERPLayout title="Job Card">
        <div className="flex items-center justify-center py-32">
          <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
        </div>
      </ERPLayout>
    );
  }

  if (!job) {
    return (
      <ERPLayout title="Job Card">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <button onClick={() => navigate("/job-cards")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Job Card Not Found</h1>
          </div>
          {error && (
            <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-fill text-amber-500" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-amber-700">Unable to load job card from server</p>
                <p className="text-xs text-amber-600 mt-0.5 break-all">Error: {error}</p>
                <p className="text-xs text-amber-600 mt-1">
                  The job card may have been deleted or the server is temporarily unavailable.
                </p>
              </div>
              <button 
                onClick={() => { setError(""); fetchJob(); }} 
                className="flex-shrink-0 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg text-xs text-amber-700 font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-refresh-line mr-1" />Retry
              </button>
            </div>
          )}
          <div className="text-center py-20 text-gray-500">
            <i className="ri-file-list-3-line text-4xl text-gray-300 mb-3 block" />
            <p>Job card #{id} could not be loaded.</p>
            <button 
              onClick={() => navigate("/job-cards")} 
              className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer"
            >
              Back to Job Cards
            </button>
          </div>
        </div>
      </ERPLayout>
    );
  }

  const currentStepIdx = statusSteps.indexOf(normalizeStatus(status));

  return (
    <ERPLayout title={`Job Card — ${job.jobNo}`}>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/job-cards")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer mt-0.5">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-gray-900">{job.title}</h1>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[status]}`}>{status}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${priorityColors[job.priority]}`}>{job.priority}</span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5 font-mono">{job.jobNo} · {job.client}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {saved && <span className="text-sm text-amber-600 font-medium flex items-center gap-1"><i className="ri-checkbox-circle-line" />Saved</span>}
            <Link to={`/job-cards/${job.id}/print`} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>Print
            </Link>
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap disabled:opacity-60">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}`} />
                  </span>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line" /></span>Edit
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <i className="ri-error-warning-line" />{error}
          </div>
        )}

        {/* Status Progress Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm text-gray-800">Job Progress</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Progress:</span>
              {isEditing ? (
                <input type="number" min={0} max={100} value={progress}
                  onChange={(e) => setProgress(Number(e.target.value))}
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
              ) : (
                <span className="text-sm font-bold text-gray-900">{progress}%</span>
              )}
            </div>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-5">
            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-100 z-0" />
            {statusSteps.map((step, idx) => (
              <div key={step} className="flex flex-col items-center gap-2 z-10">
                <button
                  onClick={() => handleStatusUpdate(step)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    idx <= currentStepIdx ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-400"
                  } ${isEditing ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
                >
                  {idx < currentStepIdx ? <i className="ri-check-line text-sm" /> : idx + 1}
                </button>
                <span className={`text-xs font-medium whitespace-nowrap ${idx <= currentStepIdx ? "text-amber-600" : "text-gray-400"}`}>{step}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {/* Main Content */}
          <div className="col-span-2 space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
              {(["overview", "timeline", "notes", "files", "deliverables"] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer capitalize whitespace-nowrap ${activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                  {tab === "deliverables" ? `Deliverables${deliverables.length > 0 ? ` (${deliverables.length})` : ""}` : tab}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-sm text-gray-800 mb-4">Project Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Client - Dropdown when editing */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Client</p>
                      {isEditing ? (
                        <select
                          value={editData.clientId ?? job.clientId}
                          onChange={(e) => {
                            const clientId = Number(e.target.value);
                            const selectedClient = clients.find(c => c.id === clientId);
                            setEditData((p) => ({ 
                              ...p, 
                              clientId,
                              client: selectedClient?.name ?? p.client ?? job.client
                            }));
                          }}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 bg-white"
                        >
                          <option value="">Select Client</option>
                          {clients.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.company ? ` (${c.company})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.client}</p>
                      )}
                    </div>

                    {/* Contact Person */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Contact Person</p>
                      {isEditing ? (
                        <input type="text"
                          value={(editData.clientContact as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, clientContact: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.clientContact}</p>
                      )}
                    </div>

                    {/* Contact Person Phone */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Contact Person Phone</p>
                      {isEditing ? (
                        <input type="tel"
                          value={(editData.contactPersonPhone as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, contactPersonPhone: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.contactPersonPhone || "—"}</p>
                      )}
                    </div>

                    {/* Location */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Location</p>
                      {isEditing ? (
                        <input type="text"
                          value={(editData.location as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, location: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.location}</p>
                      )}
                    </div>

                    {/* Area / Extent */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Area / Extent</p>
                      {isEditing ? (
                        <input type="text"
                          value={(editData.area as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, area: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.area}</p>
                      )}
                    </div>

                    {/* Priority - Dropdown when editing */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Priority</p>
                      {isEditing ? (
                        <select
                          value={editData.priority ?? job.priority}
                          onChange={(e) => setEditData((p) => ({ ...p, priority: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 bg-white"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.priority}</p>
                      )}
                    </div>

                    {/* Assigned Date */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Assigned Date</p>
                      {isEditing ? (
                        <input type="date"
                          value={(editData.assignedDate as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, assignedDate: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.assignedDate}</p>
                      )}
                    </div>

                    {/* Due Date */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Due Date</p>
                      {isEditing ? (
                        <input type="date"
                          value={(editData.dueDate as string) || ""}
                          onChange={(e) => setEditData((p) => ({ ...p, dueDate: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">{job.dueDate}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm text-gray-800">Services</h3>
                    {isEditing && (
                      <span className="text-xs text-gray-400">
                        {(editData.services ?? job.services).length} selected
                      </span>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {SERVICE_OPTIONS.map((svc) => {
                        const selected = (editData.services ?? job.services).includes(svc.label);
                        return (
                          <button
                            key={svc.label}
                            type="button"
                            onClick={() => {
                              const current = editData.services ?? job.services ?? [];
                              const updated = selected
                                ? current.filter((s) => s !== svc.label)
                                : [...current, svc.label];
                              setEditData((p) => ({ ...p, services: updated }));
                            }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer text-left ${
                              selected
                                ? "border-amber-400 bg-amber-50 text-amber-700"
                                : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200"
                            }`}
                          >
                            <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${selected ? "bg-amber-500 border-amber-500" : "border-gray-300"}`}>
                              {selected && <i className="ri-check-line text-white text-xs" />}
                            </span>
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className={`${svc.icon} text-sm ${selected ? "text-amber-600" : "text-gray-400"}`} />
                            </span>
                            {svc.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {job.services.map((s) => (
                        <span key={s} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">{s}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-sm text-gray-800 mb-3">Remarks</h3>
                  {isEditing ? (
                    <textarea rows={3} maxLength={500}
                      value={(editData.remarks as string) || ""}
                      onChange={(e) => setEditData((p) => ({ ...p, remarks: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
                  ) : (
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4 border border-gray-100">{job.remarks}</p>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === "timeline" && (
              <ActivityTimeline
                jobNo={job.jobNo}
                jobTitle={job.title}
                assignedDate={job.assignedDate}
                status={status}
                fieldTeam={job.fieldTeam}
              />
            )}

            {/* Notes Tab */}
            {activeTab === "notes" && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-sm text-gray-800 mb-4">Add Note</h3>
                  <textarea rows={3} maxLength={500} placeholder="Add a note, observation, or update..."
                    value={newNote} onChange={(e) => setNewNote(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-400">{newNote.length}/500</span>
                    <button onClick={addNote} className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line" /></span>Post Note
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {notes.map((note) => (
                    <div key={note.id} className="bg-white rounded-xl border border-gray-100 p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">{note.avatar}</div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{note.author}</p>
                          <p className="text-xs text-gray-400">{note.date}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{note.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Files Tab - Shows Deliverables as Attached Files */}
            {activeTab === "files" && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm text-gray-800">Attached Files</h3>
                  <label className="flex items-center gap-1.5 text-xs text-amber-600 font-medium cursor-pointer hover:text-amber-700 whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line" /></span>Upload File
                    <input type="file" className="hidden" />
                  </label>
                </div>
                {deliverables.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-folder-open-line text-4xl text-gray-200" />
                    </div>
                    <p className="text-sm text-gray-400">No files attached to this job</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {deliverables.map((file) => {
                      const ft = fileTypeIcon[file.fileType] || { icon: "ri-file-line", color: "#6b7280" };
                      return (
                        <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                          <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-gray-100">
                            <i className={`${ft.icon} text-lg`} style={{ color: ft.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{file.fileName}</p>
                            <p className="text-xs text-gray-400">{file.size} · {toDateStr(file.uploadedAt)} · {file.uploadedBy}</p>
                          </div>
                          <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 cursor-pointer">
                            <i className="ri-download-2-line text-sm" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Deliverables Tab */}
            {activeTab === "deliverables" && (
              <JobDeliverablesTab jobId={job.id} jobNo={job.jobNo} deliverables={deliverables} />
            )}
          </div>

          {/* Right Sidebar */}
          <div className="col-span-1 space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-sm text-gray-800 mb-4">Quick Info</h3>
              <div className="space-y-3">
                {[
                  { icon: "ri-calendar-line",       label: "Assigned", value: job.assignedDate },
                  { icon: "ri-calendar-close-line", label: "Due Date", value: job.dueDate },
                  { icon: "ri-map-pin-line",         label: "Location", value: job.location },
                  { icon: "ri-ruler-line",           label: "Area",     value: job.area },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-gray-500 text-sm`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm font-medium text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Field Team */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-gray-800">Field Team</h3>
                {isEditing && (
                  <span className="text-xs text-amber-600 font-medium">{employeesLoading ? "Loading..." : "Click to assign"}</span>
                )}
              </div>
              {employeesLoading && isEditing ? (
                <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-amber-500" />
                  Loading staff...
                </div>
              ) : isEditing ? (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {employees.filter((e) =>
                    ["Field Operations", "Lab", "field operations", "lab"].includes(e.department)
                  ).length === 0 ? (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-400">
                      <i className="ri-user-search-line text-gray-300" />
                      No field staff found
                    </div>
                  ) : (
                    employees
                      .filter((e) =>
                        ["Field Operations", "Lab", "field operations", "lab"].includes(e.department)
                      )
                      .map((emp) => {
                        const selected = ((editData as Record<string, unknown>).fieldTeamIds as number[] ?? job.fieldTeamIds).includes(emp.id);
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => {
                              const cur = ((editData as Record<string, unknown>).fieldTeamIds as number[] ?? job.fieldTeamIds);
                              const updated = cur.includes(emp.id)
                                ? cur.filter((id) => id !== emp.id)
                                : [...cur, emp.id];
                              setEditData((p) => ({ ...p, fieldTeamIds: updated }));
                            }}
                            className={`flex items-center gap-2 p-2 rounded-xl border transition-all cursor-pointer text-left w-full ${
                              selected
                                ? "border-amber-400 bg-amber-50"
                                : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"
                            }`}
                          >
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                              style={{ background: selected ? "#f59e0b" : "#e5e7eb", color: selected ? "#fff" : "#6b7280" }}>
                              {emp.avatar}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-xs font-semibold truncate ${selected ? "text-amber-800" : "text-gray-700"}`}>
                                {emp.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate capitalize">{emp.role.replace(/_/g, " ")}</p>
                            </div>
                            {selected && (
                              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                                <i className="ri-check-line text-white text-xs" />
                              </span>
                            )}
                          </button>
                        );
                      })
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {job.fieldTeam.map((member) => (
                    <div key={member} className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {member.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <span className="text-sm text-gray-700">{member}</span>
                    </div>
                  ))}
                  {job.fieldTeam.length === 0 && (
                    <p className="text-xs text-gray-400 italic">No field team assigned</p>
                  )}
                </div>
              )}
            </div>

            {/* Office Team */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-gray-800">Office Team</h3>
                {isEditing && (
                  <span className="text-xs text-amber-600 font-medium">{employeesLoading ? "Loading..." : "Click to assign"}</span>
                )}
              </div>
              {employeesLoading && isEditing ? (
                <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-amber-500" />
                  Loading staff...
                </div>
              ) : isEditing ? (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {employees.filter((e) =>
                    ["Office", "Management", "office", "management"].includes(e.department)
                  ).length === 0 ? (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-400">
                      <i className="ri-user-search-line text-gray-300" />
                      No office staff found
                    </div>
                  ) : (
                    employees
                      .filter((e) =>
                        ["Office", "Management", "office", "management"].includes(e.department)
                      )
                      .map((emp) => {
                        const selected = ((editData as Record<string, unknown>).officeTeamIds as number[] ?? job.officeTeamIds).includes(emp.id);
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => {
                              const cur = ((editData as Record<string, unknown>).officeTeamIds as number[] ?? job.officeTeamIds);
                              const updated = cur.includes(emp.id)
                                ? cur.filter((id) => id !== emp.id)
                                : [...cur, emp.id];
                              setEditData((p) => ({ ...p, officeTeamIds: updated }));
                            }}
                            className={`flex items-center gap-2 p-2 rounded-xl border transition-all cursor-pointer text-left w-full ${
                              selected
                                ? "border-amber-400 bg-amber-50"
                                : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"
                            }`}
                          >
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                              style={{ background: selected ? "#f59e0b" : "#e5e7eb", color: selected ? "#fff" : "#6b7280" }}>
                              {emp.avatar}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-xs font-semibold truncate ${selected ? "text-amber-800" : "text-gray-700"}`}>
                                {emp.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate capitalize">{emp.role.replace(/_/g, " ")}</p>
                            </div>
                            {selected && (
                              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                                <i className="ri-check-line text-white text-xs" />
                              </span>
                            )}
                          </button>
                        );
                      })
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {job.officeTeam.map((member) => (
                    <div key={member} className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gray-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {member.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <span className="text-sm text-gray-700">{member}</span>
                    </div>
                  ))}
                  {job.officeTeam.length === 0 && (
                    <p className="text-xs text-gray-400 italic">No office team assigned</p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Link to={`/job-cards/${job.id}/print`}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>Print Job Card
              </Link>
              <Link to="/billing/invoice/new"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-50 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-100 cursor-pointer whitespace-nowrap border border-amber-100">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bill-line" /></span>Generate Invoice
              </Link>
              <Link to="/field-tracking"
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-user-line" /></span>Field Tracking
              </Link>
            </div>
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
